import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { baseQuery, getBaseQueryWithReauth } from 'redux/baseQueryAuth';
import {
  type AnalystFilterOptions,
  type AnalystFiltersParams,
  type AnalystMetrics,
  type AnalystSettlement,
  type MerchantBalance,
  type Reconciliation,
  type SettlementCreateBody,
  type SettlementStatus,
  type SettlementsParams,
  type SettlementsResponse,
  type TraderBalance,
} from '../types/AnalystTypes';

export const cleanParams = <T extends object>(params: T) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));

export const AnalystApi = createApi({
  reducerPath: 'AnalystApi',
  tagTypes: ['Settlements', 'Balances'],
  baseQuery: getBaseQueryWithReauth(baseQuery),
  endpoints: (build) => ({
    getAnalystFilters: build.query<AnalystFilterOptions, void>({
      query: () => ({ url: '/analyst/filters/', method: 'GET' }),
    }),
    getAnalystMetrics: build.query<AnalystMetrics, AnalystFiltersParams>({
      query: (params) => ({ url: '/analyst/metrics/', method: 'GET', params: cleanParams(params) }),
    }),
    getMerchantBalances: build.query<MerchantBalance[], void>({
      query: () => ({ url: '/analyst/balances/merchants/', method: 'GET' }),
      providesTags: ['Balances'],
    }),
    getTraderBalances: build.query<TraderBalance[], void>({
      query: () => ({ url: '/analyst/balances/traders/', method: 'GET' }),
    }),
    getReconciliation: build.query<Reconciliation, { merchant: string; date_from: string; date_to: string }>({
      query: (params) => ({ url: '/analyst/reconciliation/', method: 'GET', params }),
      providesTags: ['Balances'],
    }),
    exportReconciliation: build.query<string, { merchant: string; date_from: string; date_to: string }>({
      query: (params) => ({
        url: '/analyst/reconciliation/export/',
        method: 'GET',
        params,
        // ошибка приходит JSON-ом, файл — бинарём; object URL скачивает страница
        responseHandler: async (response) => {
          if (!response.ok) return response.json();
          const blob = await response.blob();
          return window.URL.createObjectURL(blob);
        },
      }),
    }),
    getPlatformWallets: build.query<Array<{ id: string; name: string; address: string }>, void>({
      query: () => ({ url: '/analyst/platform_wallets/', method: 'GET' }),
    }),
    getSettlements: build.query<SettlementsResponse, SettlementsParams>({
      query: (params) => ({ url: '/analyst/settlements/', method: 'GET', params: cleanParams(params) }),
      providesTags: ['Settlements'],
    }),
    createSettlement: build.mutation<AnalystSettlement, SettlementCreateBody>({
      query: (body) => ({ url: '/analyst/settlements/', method: 'POST', body }),
      invalidatesTags: ['Settlements', 'Balances'],
    }),
    changeSettlementStatus: build.mutation<AnalystSettlement, { id: string; status: SettlementStatus; comment?: string }>({
      query: ({ id, ...body }) => ({ url: `/analyst/settlements/${id}/status/`, method: 'POST', body }),
      invalidatesTags: ['Settlements', 'Balances'],
    }),
  }),
});

export const {
  useGetAnalystFiltersQuery,
  useGetAnalystMetricsQuery,
  useGetMerchantBalancesQuery,
  useGetTraderBalancesQuery,
  useGetReconciliationQuery,
  useLazyExportReconciliationQuery,
  useGetPlatformWalletsQuery,
  useGetSettlementsQuery,
  useCreateSettlementMutation,
  useChangeSettlementStatusMutation,
} = AnalystApi;
